/**
 * Filing a draft chain of title against the server, once, however many times
 * Submit is pressed.
 *
 * Shared by the register wizard and the work record because both write the
 * same sequence — entry, ONDO id, verification, comments, asks, documents —
 * and both must survive a failure halfway. The `WriteLedger` is what makes
 * that true: every step is keyed before the next runs, the caller holds it in
 * a ref so it outlives the render after a failure, and a refusal the server
 * would only repeat (403, 409, 422) is settled rather than retried. The
 * payloads come from `provenance.model.ts`; the API calls are `api.ts`'s.
 *
 * `ensureWork` is the one entry here that is not about provenance, and it is
 * here because a duplicate work is a second painting in the catalogue with no
 * constraint to refuse it — React state is not a guard that survives.
 */

import { ApiError } from "./api.ts";
import {
  addProvenanceEntry,
  attachProvenanceOndoId,
  attestProvenance,
  commentOnProvenance,
  registerWork,
  requestProvenanceVerification,
} from "./api.ts";
import type { RegisterWorkRequest } from "@ondo/shared";
import { uploadProvenanceDocuments } from "./provenanceDocuments.ts";
import { askBody, toEntryPayloads } from "../provenance/provenance.model.ts";
import type { DraftEntry, ProvenanceItem } from "../provenance/provenance.model.ts";

/**
 * What has already been filed, kept across attempts.
 *
 * `entries` maps a local draft id to the row it became; `done` holds a key per
 * unit of work that is settled — accepted, or refused in a way the server will
 * repeat. Held by the caller in a ref, because the guard is worthless if it is
 * rebuilt on the render that follows the failure.
 */
export interface WriteLedger {
  /**
   * The work everything else attaches to, once it exists.
   *
   * The one entry in this ledger that is not about provenance, and the reason it
   * lives here rather than in React state. Opening a draft registration could
   * afford to key off `useState`: a duplicate was a row nobody had seen, and a
   * PRE number was all that was wasted. A duplicate WORK is a second painting in
   * the collector's catalogue — `ondo.register_work()` will happily make one,
   * there is no uniqueness constraint to 409 on, and until 20260804090700 there
   * was no way to remove one at all. So the guard has to survive the render that
   * follows a failure, beside every other guard.
   */
  work: string | null;
  entries: Map<string, string>;
  done: Set<string>;
}

export function newLedger(): WriteLedger {
  return { work: null, entries: new Map(), done: new Set() };
}

/**
 * Creates the work, once, however many times Confirm is pressed.
 *
 * Returns the id it created or the one it created before. Separate from
 * `writeDraftProvenance` because it is the thing everything else hangs off: the
 * images have nowhere to go and the chain of title has nothing to describe until
 * this has happened.
 */
export async function ensureWork(
  collectionId: string,
  body: RegisterWorkRequest,
  ledger: WriteLedger,
): Promise<string> {
  if (ledger.work) return ledger.work;

  const work = await registerWork(collectionId, body);
  ledger.work = work.id;
  return work.id;
}

/**
 * Files the claims that are still local, then everything their author attached
 * to them, then the bytes.
 *
 * EVERY STEP IS KEYED IN THE LEDGER BEFORE THE NEXT ONE RUNS, and that is not
 * decoration. Before this existed the wizard guarded only the entry insert, and
 * a second Submit after a failed image upload re-called
 * `attach_provenance_principal` — whose RPC updates `where owner_principal_id is
 * null` and raises 42501 when there is nothing to update. The wizard was then
 * stuck forever. The two `_one_live` partial unique indexes turn a repeated
 * stance or ask into a 409, and a repeated comment or document is filed
 * silently and permanently.
 *
 * Order: entry, ONDO id, verification, comments, asks, documents. The acts go
 * before the bytes because they are content, and because a stalled 25MB upload
 * should not be what stands between an author and their own signature.
 *
 * REFUSALS ARE SETTLED, NOT RETRIED. A queued ask can name an id that does not
 * exist, one the caller acts as, or one already asked; all three come back 403
 * with the same deliberately uninformative message, and none is checkable from a
 * browser that has no directory. Left to retry, the next Submit re-attempts the
 * failing ask and fails identically — stuck again, for a different reason. So a
 * 403, 409 or 422 marks the key done, while anything else (offline, 5xx) is
 * rethrown so the retry re-attempts it. Of those three only 403 and 422 produce
 * a sentence: a 409 means the row is already there.
 *
 * The entry insert itself is never softened. An entry that will not save is a
 * registration that must not proceed.
 */
export async function writeDraftProvenance(
  items: ProvenanceItem[],
  workId: string,
  ledger: WriteLedger,
): Promise<{ refusals: string[] }> {
  const refusals: string[] = [];

  const payloads = toEntryPayloads(items, workId).filter(
    (payload) => !ledger.entries.has(payload.localId),
  );

  // The API answers with the whole chain rather than the row, because adding a
  // link renumbers the ones after it. Which one is new is therefore the id that
  // was not there a moment ago, not the last in the list — a collector filling
  // in a chain out of order can insert one anywhere.
  const seen = new Set(ledger.entries.values());
  for (const payload of payloads) {
    const chain = await addProvenanceEntry(payload.body);
    const added = chain.find((entry) => !seen.has(entry.id));
    if (added) {
      ledger.entries.set(payload.localId, added.id);
      seen.add(added.id);
    }
  }

  for (const item of items) {
    if (item.kind !== "draft") continue;

    const draft = item.entry;
    const entryId = ledger.entries.get(draft.id);
    if (!entryId) continue;

    const step = async (key: string, what: string, act: () => Promise<unknown>) => {
      if (ledger.done.has(key)) return;
      try {
        await act();
      } catch (cause: unknown) {
        if (!isSettled(cause)) throw cause;
        // A 409 is not a refusal. Both `_one_live` partial unique indexes answer
        // it, and both mean the state is already what was asked for — the row
        // went in on an earlier attempt whose response never made it back. Say
        // nothing and move on; telling a collector their verification "was not
        // recorded" when it is on the record would be worse than silence.
        if (cause.status !== 409) refusals.push(describeRefusal(draft, what, cause));
      }
      ledger.done.add(key);
    };

    if (draft.ondoId !== null) {
      await step(`${draft.id}:ondo`, "the ONDO id on", () =>
        attachProvenanceOndoId(entryId, { ondoId: draft.ondoId ?? "" }),
      );
    }

    if (draft.acts.stance === "verify") {
      await step(`${draft.id}:stance`, "your verification of", () =>
        attestProvenance(entryId, { stance: "verify" }),
      );
    }

    for (const comment of draft.acts.comments) {
      await step(`${draft.id}:${comment.id}`, "a comment on", () =>
        commentOnProvenance(entryId, { body: comment.body }),
      );
    }

    for (const request of draft.acts.requests) {
      await step(`${draft.id}:${request.id}`, `the request to ${request.party.value} about`, () =>
        requestProvenanceVerification(entryId, askBody(request.party)),
      );
    }

    for (const [position, document] of draft.documents.entries()) {
      await step(`${draft.id}:${document.id}`, `${document.file.name} on`, () =>
        uploadProvenanceDocuments([
          { entryId, file: document.file, kind: document.kind, position },
        ]),
      );
    }
  }

  return { refusals };
}

/**
 * Has the server answered, or did the request never land?
 *
 * The difference decides whether a retry is a second chance or a second
 * identical failure. A 403 from a definer, a 409 from a partial unique index and
 * a 422 from a check constraint are all answers; a 0 (unreachable) or a 5xx is
 * not.
 */
function isSettled(cause: unknown): cause is ApiError {
  return cause instanceof ApiError && [403, 409, 422].includes(cause.status);
}

function describeRefusal(draft: DraftEntry, what: string, cause: ApiError): string {
  const owner = draft.owner.trim() === "" ? "an entry" : draft.owner.trim();
  return `${what} ${owner} was not recorded: ${cause.message}`;
}
